import { useEffect, useState } from "react";
import { useDogs } from "@/context/DogsContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { SectionTitle } from "@/components/app/Bits";
import EmptyDogState from "@/components/app/EmptyDogState";
import { Target, Calendar, Compass, Star, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const HORIZONS = [
  { key: "three_month", label: "3 month goals", icon: Calendar, hint: "Specific, achievable wins for this season." },
  { key: "one_year", label: "1 year goals", icon: Target, hint: "Bigger milestones — what does this year look like at its best?" },
  { key: "five_year", label: "5 year goals", icon: Compass, hint: "Where do you want this partnership to be?" },
  { key: "lifetime", label: "Lifetime goals", icon: Star, hint: "The dream — the legacy you'd be proud of." },
];

const ACTION_FIELDS = [
  { key: "action_1", label: "Action 1", placeholder: "e.g. Hire a handling coach for monthly lessons" },
  { key: "action_2", label: "Action 2", placeholder: "e.g. Build a 4-day-a-week conditioning routine" },
  { key: "action_3", label: "Action 3", placeholder: "e.g. Enter at least one specialty before September" },
];

const EMPTY = {
  three_month: "", one_year: "", five_year: "", lifetime: "",
  action_1: "", action_2: "", action_3: "",
};

export default function GoalsPage() {
  const { activeDog } = useDogs();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!activeDog) return;
    api.get("/goals", { params: { dog_id: activeDog.id } }).then(({ data }) => {
      setForm({
        three_month: data.three_month || "",
        one_year: data.one_year || "",
        five_year: data.five_year || "",
        lifetime: data.lifetime || "",
        action_1: data.action_1 || "",
        action_2: data.action_2 || "",
        action_3: data.action_3 || "",
      });
    });
  }, [activeDog]);

  if (!activeDog) return <EmptyDogState>{null}</EmptyDogState>;

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/goals", { dog_id: activeDog.id, ...form });
      toast.success("Goals saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-10" data-testid="goals-page">
      <SectionTitle
        overline="Where you're going"
        title="Goals"
        action={
          <Button onClick={save} disabled={saving} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="save-goals-button">
            {saving ? "Saving…" : "Save goals"}
          </Button>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
        {HORIZONS.map((h) => {
          const Icon = h.icon;
          return (
            <Card key={h.key} className="border-border shadow-none rounded-lg" data-testid={`goal-${h.key}`}>
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-3">
                  <div className="h-9 w-9 rounded-md bg-secondary text-secondary-foreground flex items-center justify-center">
                    <Icon size={16} />
                  </div>
                  <div>
                    <Label className="font-display text-base font-bold tracking-tight">{h.label}</Label>
                    <p className="text-xs text-muted-foreground">{h.hint}</p>
                  </div>
                </div>
                <Textarea
                  rows={6}
                  value={form[h.key]}
                  onChange={(e) => setForm({ ...form, [h.key]: e.target.value })}
                  className="text-sm leading-6"
                  data-testid={`goal-input-${h.key}`}
                  placeholder="One per line, or whatever format helps you think clearly…"
                />
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Action items */}
      <Card className="border-border shadow-none rounded-lg bg-secondary" data-testid="goals-actions-card">
        <CardContent className="p-6 sm:p-8">
          <div className="flex items-start gap-3 mb-5">
            <div className="h-10 w-10 rounded-md bg-primary text-primary-foreground flex items-center justify-center shrink-0">
              <ArrowRight size={18} />
            </div>
            <div>
              <h3 className="font-display text-xl font-bold tracking-tight">This year's moves</h3>
              <p className="text-sm text-muted-foreground mt-0.5">
                Three concrete things that will move you toward your goals in the next 12 months.
              </p>
            </div>
          </div>
          <div className="space-y-3">
            {ACTION_FIELDS.map((a, idx) => (
              <div key={a.key} className="flex items-center gap-3">
                <div className="h-7 w-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-medium shrink-0">
                  {idx + 1}
                </div>
                <Input
                  value={form[a.key]}
                  onChange={(e) => setForm({ ...form, [a.key]: e.target.value })}
                  placeholder={a.placeholder}
                  data-testid={`goal-action-${idx + 1}`}
                  className="bg-card"
                />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
